import { goNoGoService } from '@/services/go-nogo.service';
import { toMoment } from '@/util/date-util';
import axios from 'axios';

const initialState = () => ({
  opportunityVotes: [],
  opportunityVoteReasons: [],
  // Distinguishes "not fetched yet" from "fetched, tenant has none configured", so callers can
  // dedupe the request without re-fetching forever on a tenant with no presets.
  voteReasonsLoaded: false,
  currentUserVote: null,
  currentUser: null,
  finalDecision: null,
  finalDecisionHistory: [],
  totalVotes: 0,
  votesCount: 0,
  users: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  async fetchOpportunityVotes({ commit }, { opportunityId, users, currentUser }) {
    try {
      const opportunityVotes = await goNoGoService.getOpportunityVotes({ opportunityId });
      const opportunityVoteReasons = await goNoGoService.getOpportunityVoteReasons();
      commit('SET_CURRENT_USER_VOTE', { currentUser });

      opportunityVotes.forEach((opportunityVote) => {
        if (opportunityVote.reasons && opportunityVote.reasons.length > 0) {
          opportunityVote.transformedReasons = [];
          opportunityVote.reasons.forEach((reason) => {
            const foundReason = opportunityVoteReasons.find((r) => r.reason === reason);
            if (foundReason) {
              opportunityVote.transformedReasons.push(foundReason);
            } else {
              let customReason = {
                reason: reason,
                voteType: 'NO',
              };
              opportunityVote.transformedReasons.push(customReason);
            }
          });
        }

        const user = users.find((u) => u.id === opportunityVote.userId);
        opportunityVote.user = user || null;

        if (user && user.id === currentUser.id) {
          opportunityVote.user.currentUser = true;
        }

        if (opportunityVote.vote === 'NO') {
          opportunityVote.vote = 'NOGO';
        } else if (opportunityVote.vote === 'YES') {
          opportunityVote.vote = 'GO';
        } else if (opportunityVote.vote === 'NEUTRAL') {
          opportunityVote.vote = 'INDIFFERENT';
        }

        if (currentUser.id === opportunityVote.userId) {
          const currentUserVote = opportunityVotes.find((vote) => vote.userId === currentUser.id) || null;
          commit('SET_CURRENT_USER_VOTE', { currentUserVote, currentUser });
        }
      });

      const stats = calculateStats(opportunityVotes);
      commit('UPDATE_STATS', stats);

      commit('SET_USERS', users);
      commit('SET_OPPORTUNITY_VOTES', opportunityVotes);

      commit('SET_OPPORTUNITY_VOTE_REASONS', opportunityVoteReasons);
      commit('GET_VOTES_SUCCESS', opportunityVotes);
    } catch (error) {
      if (!axios.isCancel(error)) {
        commit('ERROR', error);
        throw error;
      }
    }
  },
  getOpportunityVoteReasons({ commit }) {
    commit('GET_VOTE_REASONS');
    return goNoGoService.getOpportunityVoteReasons().then(
      (opportunityVoteReasons) => {
        commit('GET_VOTE_REASONS_SUCCESS', opportunityVoteReasons);
        return opportunityVoteReasons;
      },
      (error) => {
        commit('GET_VOTE_REASONS_ERROR', error);
        return error;
      },
    );
  },
  /**
   * The POST accepts the vote itself, so a first vote is one request and nothing is written while
   * the dialog is merely open — it used to pre-POST a pending invitation just to obtain an id.
   */
  async castOpportunityVote({ commit, state, dispatch }, { opportunityId, userId, userVote, vote, reasons }) {
    if (userVote && userVote.id) {
      return dispatch('updateOpportunityVote', { opportunityId, userVote, vote, reasons });
    }

    const created = await goNoGoService.createOpportunityVote({ opportunityId, userId, vote, reasons });
    // A copy: the list keeps raw API vote values, and the mutation below rewrites them in place.
    commit('opportunities/ADD_OPPORTUNITY_VOTE', { opportunityId, opportunityVote: { ...created } }, { root: true });
    commit('UPDATE_OPPORTUNITY_VOTE_SUCCESS', created);
    commit('UPDATE_STATS', calculateStats(state.opportunityVotes));
    commit('SET_CURRENT_USER_VOTE', { currentUserVote: created, currentUser: state.currentUser });

    return created;
  },
  updateOpportunityVote({ commit, state }, { opportunityId, userVote, vote, reasons }) {
    if (!userVote.id) return;
    return goNoGoService.updateOpportunityVote({ opportunityId, voteId: userVote.id, vote, reasons }).then(
      (opportunityVote) => {
        commit(
          'opportunities/UPDATE_OPPORTUNITY_VOTE',
          { opportunityId, userId: userVote.userId, vote },
          { root: true },
        );
        commit('UPDATE_OPPORTUNITY_VOTE_SUCCESS', opportunityVote);

        const stats = calculateStats(state.opportunityVotes);
        commit('UPDATE_STATS', stats);

        if (state.currentUser.id === opportunityVote.userId) {
          commit('SET_CURRENT_USER_VOTE', { currentUserVote: opportunityVote, currentUser: state.currentUser });
        }

        return opportunityVote;
      },
      (error) => {
        // Surfaced by the caller — notably the 409 raised once a final decision locks voting.
        throw error;
      },
    );
  },
  deleteOpportunityVote({ commit, state }, { opportunityId, voteId, selfVote }) {
    if (!voteId) return;
    return goNoGoService.deleteOpportunityVote({ opportunityId, voteId }).then(
      () => {
        commit('DELETE_OPPORTUNITY_VOTE_SUCCESS', voteId);
        commit('opportunities/DELETE_OPPORTUNITY_VOTE_SUCCESS', { opportunityId, voteId }, { root: true });

        const stats = calculateStats(state.opportunityVotes);
        commit('UPDATE_STATS', stats);
        if (selfVote) {
          commit('RESET_CURRENT_USER_VOTE');
        }

        return voteId;
      },
      (error) => {
        console.error('Error deleting opportunity vote:', error);
        throw error;
      },
    );
  },
  addOpportunityVoteReason({ commit }, { voteType, reason }) {
    return goNoGoService.addOpportunityVoteReason({ voteType, reason }).then(
      (opportunityVoteReason) => {
        commit('ADD_OPPORTUNITY_VOTE_REASON_SUCCESS', opportunityVoteReason);

        return opportunityVoteReason;
      },
      (error) => {
        console.error('Error adding opportunity vote reason:', error);
        throw error;
      },
    );
  },
  updateOpportunityVoteReason({ commit }, { id, voteType, reason }) {
    return goNoGoService.updateOpportunityVoteReason({ id, voteType, reason }).then(
      (updatedVoteReason) => {
        commit('UPDATE_OPPORTUNITY_VOTE_REASON', updatedVoteReason);
        return updatedVoteReason;
      },
      (error) => {
        console.error('Error updating opportunity vote reason:', error);
        throw error;
      },
    );
  },
  deleteOpportunityVoteReason({ commit }, id) {
    return goNoGoService.deleteOpportunityVoteReason(id).then(
      () => {
        commit('DELETE_OPPORTUNITY_VOTE_REASON', id);
      },
      (error) => {
        console.error('Error deleting opportunity vote reason:', error);
        throw error;
      },
    );
  },
  addCustomOpportunityVoteReason({ commit }, { voteType, reason }) {
    const currentDate = new Date();
    const formattedDate = toMoment(currentDate);

    let customOpportunityVoteReason = {
      createdAt: formattedDate,
      reason: reason,
      voteType: voteType,
      updatedAt: null,
    };

    commit('ADD_OPPORTUNITY_VOTE_REASON_SUCCESS', customOpportunityVoteReason);
    return customOpportunityVoteReason;
  },
  async inviteUserOpportunityVote({ commit, state }, { opportunityId, userId }) {
    try {
      const opportunityVote = await goNoGoService.inviteUserOpportunityVote({ opportunityId, userId });

      commit('ADD_OPPORTUNITY_VOTE_SUCCESS', opportunityVote);
      commit('opportunities/ADD_OPPORTUNITY_VOTE', { opportunityId, opportunityVote }, { root: true });
      const stats = calculateStats(state.opportunityVotes);
      commit('UPDATE_STATS', stats);

      if (state.currentUser.id === opportunityVote.userId) {
        const currentUserVote = state.opportunityVotes.find((vote) => vote.userId === state.currentUser.id) || null;
        commit('SET_CURRENT_USER_VOTE', { currentUserVote });
      }

      return opportunityVote;
    } catch (error) {
      // No `this.$toast` here: `this` is the store, so touching it replaces the real error with a
      // TypeError. Callers own the toast.
      console.error('Error inviting user:', error);
      throw error;
    }
  },
  async fetchFinalDecision({ commit }, { opportunityId }) {
    try {
      const finalDecision = await goNoGoService.getFinalDecision({ opportunityId });
      commit('SET_FINAL_DECISION', finalDecision);

      return finalDecision;
    } catch (error) {
      if (!axios.isCancel(error)) throw error;
    }
  },
  async fetchFinalDecisionHistory({ commit }, { opportunityId }) {
    try {
      const finalDecisionHistory = await goNoGoService.getFinalDecisionHistory({ opportunityId });
      commit('SET_FINAL_DECISION_HISTORY', finalDecisionHistory);

      return finalDecisionHistory;
    } catch (error) {
      if (!axios.isCancel(error)) throw error;
    }
  },
  async recordFinalDecision({ commit, dispatch }, { opportunityId, vote, reasons, comment }) {
    const finalDecision = await goNoGoService.createFinalDecision({ opportunityId, vote, reasons, comment });
    commit('SET_FINAL_DECISION', finalDecision);
    commitFinalDecisionToOpportunity(commit, opportunityId, finalDecision);
    await refreshFinalDecisionHistory(dispatch, opportunityId);

    return finalDecision;
  },
  async changeFinalDecision({ commit, dispatch }, { opportunityId, vote, reasons, comment }) {
    const finalDecision = await goNoGoService.updateFinalDecision({ opportunityId, vote, reasons, comment });
    commit('SET_FINAL_DECISION', finalDecision);
    commitFinalDecisionToOpportunity(commit, opportunityId, finalDecision);
    await refreshFinalDecisionHistory(dispatch, opportunityId);

    return finalDecision;
  },
  async deleteFinalDecision({ commit, dispatch }, { opportunityId }) {
    await goNoGoService.deleteFinalDecision({ opportunityId });
    commit('SET_FINAL_DECISION', null);
    commitFinalDecisionToOpportunity(commit, opportunityId, null);
    // The journal survives a deletion and gains a DELETE event.
    await refreshFinalDecisionHistory(dispatch, opportunityId);
  },
  setCurrentUser({ commit }, currentUser) {
    commit('SET_CURRENT_USER', currentUser);
  },
  setUsers({ commit }, users) {
    commit('SET_USERS', users);
  },
  RESET({ commit }) {
    commit('RESET_STORE');
  },
};

// Mirrors the decision onto the list and details state so the header tag and votes column react
// without a re-fetch.
function commitFinalDecisionToOpportunity(commit, opportunityId, finalDecision) {
  commit('opportunity/SET_FINAL_DECISION', { opportunityId, finalDecision }, { root: true });
  commit('opportunities/SET_FINAL_DECISION', { opportunityId, finalDecision }, { root: true });
}

// Best-effort: the write already succeeded, so a failed journal refresh must not reach the caller
// as a failed decision.
function refreshFinalDecisionHistory(dispatch, opportunityId) {
  return dispatch('fetchFinalDecisionHistory', { opportunityId }).catch(() => {});
}

/**
 * Participation only: how many were asked (`totalVotes`, pending invitations included) versus how
 * many answered. GO/NO-GO shares are derived in the component instead, so there is one
 * implementation rather than two disagreeing on the denominator.
 */
function calculateStats(opportunityVotes) {
  let totalVotes = 0;
  let votesCount = 0;

  opportunityVotes.forEach((opportunityVote) => {
    if (!opportunityVote.isPending) {
      votesCount++;
    }

    totalVotes++;
  });

  return {
    totalVotes,
    votesCount,
  };
}

function transformReasons(reasons, opportunityVoteReasons) {
  return reasons.map((reason) => {
    const foundReason = opportunityVoteReasons.find((r) => r.reason === reason);
    return foundReason || null;
  });
}

const mutations = {
  GET_VOTES(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.opportunityVotes = [];
  },
  GET_VOTES_SUCCESS(state, opportunityVotes) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };
    state.opportunityVotes = opportunityVotes;
  },
  // The list stays shown until the new one replaces it.
  GET_VOTE_REASONS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_VOTE_REASONS_SUCCESS(state, opportunityVoteReasons) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };
    state.opportunityVoteReasons = opportunityVoteReasons;
    state.voteReasonsLoaded = true;
  },
  GET_VOTE_REASONS_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  SET_OPPORTUNITY_VOTES(state, opportunityVotes) {
    state.opportunityVotes = opportunityVotes;
  },

  SET_OPPORTUNITY_VOTE_REASONS(state, opportunityVoteReasons) {
    state.opportunityVoteReasons = opportunityVoteReasons;
    state.voteReasonsLoaded = true;
  },
  SET_CURRENT_USER(state, currentUser) {
    state.currentUser = currentUser;
  },

  SET_CURRENT_USER_VOTE(state, { currentUserVote, currentUser }) {
    if (currentUserVote) {
      state.currentUserVote = currentUserVote;
    }
    if (currentUser) {
      state.currentUser = currentUser;
    }
  },

  UPDATE_STATS(state, stats) {
    state.totalVotes = stats.totalVotes;
    state.votesCount = stats.votesCount;
  },

  UPDATE_OPPORTUNITY_VOTE_SUCCESS(state, updatedOpportunityVote) {
    const transformedReasons = transformReasons(updatedOpportunityVote.reasons, state.opportunityVoteReasons);
    if (transformedReasons.length > 0) {
      updatedOpportunityVote.transformedReasons = transformedReasons;
    }

    // Update vote type
    if (updatedOpportunityVote.vote === 'NO') {
      updatedOpportunityVote.vote = 'NOGO';
    } else if (updatedOpportunityVote.vote === 'YES') {
      updatedOpportunityVote.vote = 'GO';
    } else if (updatedOpportunityVote.vote === 'NEUTRAL') {
      updatedOpportunityVote.vote = 'INDIFFERENT';
    }
    updatedOpportunityVote.user = state.currentUser;

    const index = state.opportunityVotes.findIndex((vote) => vote.id === updatedOpportunityVote.id);

    if (index !== -1) {
      state.opportunityVotes.splice(index, 1, updatedOpportunityVote);
    } else {
      state.opportunityVotes.push(updatedOpportunityVote);
    }
  },
  UPDATE_OPPORTUNITY_VOTE_REASON(state, updatedVoteReason) {
    const vote = state.opportunityVoteReasons.find((voteReason) => voteReason.id === updatedVoteReason.id);
    if (vote) {
      const index = state.opportunityVoteReasons.findIndex((reason) => reason.id === updatedVoteReason.id);
      if (index !== -1) {
        state.opportunityVoteReasons.splice(index, 1, updatedVoteReason);
      }
    }
  },
  DELETE_OPPORTUNITY_VOTE_SUCCESS(state, voteId) {
    const index = state.opportunityVotes.findIndex((vote) => vote.id === voteId);

    if (index !== -1) {
      state.opportunityVotes.splice(index, 1);
    }
  },
  DELETE_OPPORTUNITY_VOTE_REASON(state, id) {
    const index = state.opportunityVoteReasons.findIndex((voteReason) => voteReason.id === id);
    if (index !== -1) {
      state.opportunityVoteReasons.splice(index, 1);
    }
  },
  ADD_OPPORTUNITY_VOTE_REASON_SUCCESS(state, opportunityVoteReason) {
    state.opportunityVoteReasons.push(opportunityVoteReason);
  },
  ADD_OPPORTUNITY_VOTE_SUCCESS(state, opportunityVote) {
    const transformedReasons = transformReasons(opportunityVote.reasons, state.opportunityVoteReasons);
    if (transformedReasons.length > 0) {
      opportunityVote.transformedReasons = transformedReasons;
    }
    const user = state.users.find((u) => u.id === opportunityVote.userId);
    opportunityVote.user = user || null;

    state.opportunityVotes.push(opportunityVote);
  },
  RESET_CURRENT_USER_VOTE(state) {
    state.currentUserVote = null;
  },
  SET_USERS(state, users) {
    // Callers pass the tenant roster straight from its getter, which is undefined until the tenant
    // request lands. Normalising here keeps every consumer's `.find` safe.
    state.users = users || [];
  },
  SET_FINAL_DECISION(state, finalDecision) {
    state.finalDecision = finalDecision || null;
  },
  SET_FINAL_DECISION_HISTORY(state, finalDecisionHistory) {
    state.finalDecisionHistory = finalDecisionHistory || [];
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.opportunityVotes = [];
  },
  RESET_STORE(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  opportunityVotes: (state) => state.opportunityVotes,
  opportunityVoteReasons: (state) => state.opportunityVoteReasons,
  tenantOpportunityVoteReasons: (state) => state.opportunityVoteReasons.filter((reason) => reason.id != null),
  voteReasonsLoaded: (state) => state.voteReasonsLoaded,
  currentUserVote: (state) => state.currentUserVote,
  totalVotes: (state) => state.totalVotes,
  votesCount: (state) => state.votesCount,
  finalDecision: (state) => state.finalDecision,
  finalDecisionHistory: (state) => state.finalDecisionHistory,
  // Past this point the vote endpoints answer 409, so the UI hides the affordances instead.
  secondaryVotesLocked: (state) => !!state.finalDecision,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const goNoGo = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
